/**
 * Minimal Zustand-compatible store factory.
 * Eliminates external zustand dependency while providing the same API:
 * - createStore<T>((set, get) => initialState) → useStore hook
 * - useStore() returns full state, triggers re-render on changes
 * - useStore.getState() / useStore.subscribe() for external access
 */

import { useState, useEffect } from 'react'

export function createStore<T extends object>(
  initializer: (
    set: (partial: Partial<T> | ((state: T) => Partial<T>)) => void,
    get: () => T,
  ) => T,
) {
  let state: T
  const listeners = new Set<() => void>()

  const get = () => state
  const set = (partial: Partial<T> | ((s: T) => Partial<T>)) => {
    const nextPartial = typeof partial === 'function' ? partial(state) : partial
    const nextState = { ...state, ...nextPartial }
    if (!Object.is(state, nextState)) {
      state = nextState
      listeners.forEach((l) => l())
    }
  }
  const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  }

  state = initializer(set, get)

  // React hook — universally compatible (React 16+)
  function useStore(): T {
    const [, forceUpdate] = useState(0)
    useEffect(() => subscribe(() => forceUpdate((n) => n + 1)), [])
    return get()
  }
  useStore.getState = get
  useStore.setState = set
  useStore.subscribe = (cb: (state: T, prevState: T) => void) => {
    let prev = state
    return subscribe(() => {
      const next = state
      cb(next, prev)
      prev = next
    })
  }

  return useStore
}
